import moment from 'moment';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { StatusUpdate } from 'api/status-updates';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody00Secondary,
  CpBody01Primary,
  CpBody0aPlaceholder,
} from 'components/atoms/customer-portal/typography';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { CKCollabEditor } from 'components/organisms/ck-collab-editor';
import { DocumentProvider } from 'components/organisms/document/document-store';
import {
  taskSectionConfig,
  projectSummaryconfig,
  resourceListConfig,
} from 'components/organisms/status-updates/editor';
import styles from 'components/organisms/status-updates/editor/editor.module.css';
import { UnreadIcon } from 'components/organisms/status-updates/list/styles';
import { fullName } from 'utils/full-name';
import { TaskSection } from 'vendor/ckeditor/plugins/document-tasks-section';
import { ProjectSummary } from 'vendor/ckeditor/plugins/project-summary/plugin';
import { ResourceListSection } from 'vendor/ckeditor/plugins/resource-list-section';
import { VariablesUI } from 'vendor/ckeditor/plugins/variables/variables-ui';
import { CpTag } from '../../components/tags/cp-tags';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import {
  CpSectionWrapper,
  CpWidgetDescription,
  CpWidgetTitle,
} from '../common';
import { CpProjectUpdatePane } from './cp-project-update-pane';

interface CpProjectUpdatesData extends Record<string, unknown> {
  title: string;
  description: string;
  recentStatusUpdates: StatusUpdate[];
  statusUpdates: StatusUpdate[];
}

const StyledDesc = styled(CpWidgetDescription)`
  margin-bottom: var(--cp-spacing-06);
`;

const StatusUpdateStoriesWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: var(--cp-spacing-06);
  margin-bottom: var(--cp-spacing-06);
  @media (max-width: 768px) {
    overflow-x: auto;
    gap: var(--cp-spacing-04);
  }
`;

const ThumbnailWrapper = styled.div`
  width: 355px;
  max-width: 355px;
  min-width: 355px;
  height: 490px;
  background: var(--cp-white);
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--cp-spacing-05);
  cursor: pointer;

  @media (max-width: 480px) {
    max-width: 86svw;
    min-width: 86svw;
    width: 86svw;
  }
`;

const ThumbnailGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 355px;
  height: 490px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(0, 0, 0, 0.35) 75%,
    rgba(0, 0, 0, 0.5) 100%
  );
  cursor: pointer;
`;

const AvatarWrapper = styled.div`
  position: absolute;
  top: var(--cp-spacing-06);
  left: var(--cp-spacing-06);
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid var(--cp-blue-60);
  padding: var(--cp-spacing-02);
  border-radius: 100%;
  height: 100px;
  width: 100px;
  background: var(--cp-white);
  z-index: 1;
`;

const StyledAvatar = styled(Avatar)`
  width: 100%;
  height: 100%;
  border-radius: 100%;
`;

const ProjectUpdateEmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-03);
`;

const ContentWrapper = styled.div`
  width: 100%;
  background-color: var(--cp-coolest-gray-10);
  color: var(--cp-text-placeholder);
  font-size: var(--cp-text-placeholder);
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const SUContent: React.FC<{
  statusUpdate: StatusUpdate;
}> = ({ statusUpdate: statusUpdateDetails }) => {
  const presenceListElementRef = useRef<HTMLDivElement>(null);

  const fallBack = useCallback(
    () => (
      <p>
        <FormattedMessage id="editor.error" />
      </p>
    ),
    [],
  );

  return (
    <ErrorBoundary fallback={fallBack}>
      <div className={styles.presence} ref={presenceListElementRef} />

      <CKCollabEditor
        className="status-update__editor"
        onEditorReady={() => {}}
        singleUserMode
        readOnly
        customClasses={{
          editorClass: styles.ck_editor,
        }}
        extraPlugins={[
          TaskSection,
          ResourceListSection,
          VariablesUI,
          ProjectSummary,
        ]}
        extraConfig={{
          ...taskSectionConfig({
            publishedData: statusUpdateDetails?.publishedData ?? [],
          }),
          ...resourceListConfig(
            {
              publishedData: statusUpdateDetails?.publishedData ?? [],
            },
            statusUpdateDetails?.project?.projectId,
          ),
          ...projectSummaryconfig(
            {
              publishedData: statusUpdateDetails?.publishedData ?? [],
              projectId: statusUpdateDetails?.project?.projectId,
            },
            statusUpdateDetails,
          ),
        }}
        extraContextConfig={{
          publishedData: statusUpdateDetails?.publishedData ?? [],
        }}
        presenceListElementRef={presenceListElementRef}
        docId={statusUpdateDetails.document.documentId}
        projectId={statusUpdateDetails?.project?.projectId}
        onEditorFocusChange={() => {}}
        shouldHidePlaceHolder
        isStatusUpdateEditor
      />
    </ErrorBoundary>
  );
};

const SuContentWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  .ck-blurred.ck.ck-content {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
    scale: 0.4;
    width: 250%;
    height: 200vh;
  }
`;

const StatusUpdateStoryThubmnail: React.FC<{
  statusUpdate: StatusUpdate;
  onClick: () => void;
}> = ({ statusUpdate, onClick }) => {
  return (
    <ThumbnailWrapper onClick={onClick}>
      <ThumbnailGradient />
      <AvatarWrapper>
        <StyledAvatar
          src={statusUpdate?.createdBy?.photoUrl}
          alt="avatar"
          name={fullName(statusUpdate?.createdBy)}
        />
      </AvatarWrapper>
      <SuContentWrapper>
        <DocumentProvider>
          <SUContent statusUpdate={statusUpdate} />
        </DocumentProvider>
      </SuContentWrapper>
    </ThumbnailWrapper>
  );
};

const StyledUnreadIcon = styled(UnreadIcon)<{
  unread: boolean;
}>`
  position: static;
  visibility: ${(props) => (props.unread ? 'visible' : 'hidden')};
`;

const PrimaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-04);
  width: 100%;

  @media (max-width: 480px) {
    ${CpBody01Primary} {
      /* ellipsis */
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 80%;
    }
  }
`;

const SecondaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-04);
  width: 100%;
  justify-content: flex-end;
`;

const RowWrapper = styled.div`
  width: 100%;
  position: relative;
  padding: var(--cp-spacing-04) 0;
  border-top: 1px solid var(--cp-border-subtle-auto-adjust);
  &:last-child {
    border-bottom: 1px solid var(--cp-border-subtle-auto-adjust);
  }
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${CpBody01Primary}, ${CpBody0aPlaceholder} {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    ${CpTag}, ${SecondaryContent} {
      display: none;
    }
  }
`;

const Row: React.FC<{
  statusUpdate: StatusUpdate;
  onClick: () => void;
}> = ({ statusUpdate, onClick }) => {
  return (
    <RowWrapper onClick={onClick}>
      <PrimaryContent>
        <StyledUnreadIcon unread={!statusUpdate?.seenByUser} />
        <Avatar
          src={statusUpdate?.createdBy?.photoUrl}
          alt="avatar"
          name={fullName(statusUpdate?.createdBy)}
        />
        <CpBody01Primary autoAdjustColor>
          {statusUpdate?.statusUpdateTitle}
        </CpBody01Primary>
        <CpTag autoAdjustColor>
          {statusUpdate?.status?.statusValue ?? 'No status'}
        </CpTag>
      </PrimaryContent>
      <SecondaryContent>
        <CpBody0aPlaceholder autoAdjustColor>
          {statusUpdate?.createdAt
            ? moment(statusUpdate?.createdAt).format('HH:mm  MMM DD, YYYY')
            : ''}
        </CpBody0aPlaceholder>
      </SecondaryContent>
    </RowWrapper>
  );
};

const SectionTitle = styled(CpBody00Secondary)`
  margin-bottom: var(--cp-spacing-05);
`;

export const CpProjectUpdates: React.FC<CpWidgetProps> = ({
  data: _data,
  widget,
  disabled,
}) => {
  const background = useGetBackground(widget);
  const { pushToHashMap } = useCustomerPortalHashMap();

  const data = useMemo(() => _data as CpProjectUpdatesData, [_data]);

  const onStatusUpdateClick = useCallback(
    (statusUpdateId: number) => {
      pushToHashMap('cp_status_update_drawer', `${statusUpdateId}`);
    },
    [pushToHashMap],
  );

  if (!data) {
    return (
      <ProjectUpdateEmptyStateWrapper>
        <TitleWrapper>
          <CpWidgetTitle autoAdjustColor>
            <FormattedMessage id="project.update.empty_state.title" />
          </CpWidgetTitle>
          <StyledDesc autoAdjustColor>
            <FormattedMessage id="project.update.empty_state.description" />
          </StyledDesc>
        </TitleWrapper>
        <ContentWrapper>
          <FormattedMessage id="project.update.empty_state.message" />
        </ContentWrapper>
      </ProjectUpdateEmptyStateWrapper>
    );
  }

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
      <StyledDesc autoAdjustColor>{data.description}</StyledDesc>

      <SectionTitle autoAdjustColor>
        <FormattedMessage id="customer_portal.widgets.project_updates.last_3_updates" />
      </SectionTitle>

      <StatusUpdateStoriesWrapper>
        {data.recentStatusUpdates?.map?.((statusUpdate) => (
          <StatusUpdateStoryThubmnail
            key={statusUpdate.statusUpdateId}
            statusUpdate={statusUpdate}
            onClick={() => {
              onStatusUpdateClick(statusUpdate.statusUpdateId);
            }}
          />
        ))}
      </StatusUpdateStoriesWrapper>

      <SectionTitle autoAdjustColor>
        <FormattedMessage id="customer_portal.widgets.project_updates.everything_else" />
      </SectionTitle>
      {data?.statusUpdates?.map((statusUpdate) => (
        <Row
          key={statusUpdate.statusUpdateId}
          statusUpdate={statusUpdate}
          onClick={() => {
            onStatusUpdateClick(statusUpdate.statusUpdateId);
          }}
        />
      ))}
      <CpProjectUpdatePane />
    </CpSectionWrapper>
  );
};
